import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, ExternalLink, ChevronDown, UserPlus, CreditCard, Activity, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const ENDPOINTS = [
  {
    id: "registration",
    title: "Workshop Registration",
    desc: "ENGARAGE → POMEN: create a new lead when a workshop signs up.",
    method: "POST",
    path: "/v1/leads",
    icon: UserPlus,
    color: "#0D0D5B",
    sample: {
      name: "Encik Ahmad",
      email: "ahmad@autocare.com",
      phone: "+60111234567",
      company: "AutoCare Sdn Bhd",
      location: "Kuala Lumpur",
      custom_fields: { "Workshop ID": "WS_12345", Source: "ENGARAGE Registration Form" },
    },
  },
  {
    id: "subscription",
    title: "Subscription / Plan Change",
    desc: "Marks the lead as Subscribed with their plan. Send Free plan to mark a downgrade.",
    method: "POST",
    path: "/v1/subscription",
    icon: CreditCard,
    color: "#10B981",
    sample: {
      workshop_id: "WS_12345",
      email: "ahmad@autocare.com",
      plan: "Plus",
      subscriber_name: "Encik Ahmad",
      company: "AutoCare Sdn Bhd",
      subscription_id: "SUB_98765",
      subscription_date: "2026-05-29T10:00:00Z",
    },
    note: "Allowed plans: Basic · Plus · Pro · Free (downgrade)",
  },
  {
    id: "usage",
    title: "Workshop Usage",
    desc: "Reports the workshop's transaction count. Lookup is by email → phone → workshop_id.",
    method: "POST",
    path: "/v1/usage",
    icon: Activity,
    color: "#F59E0B",
    sample: {
      email: "ahmad@autocare.com",
      workshop_id: "WS_12345",
      transaction_count: 247,
      period: "2026-05",
    },
    note: "Shown on the lead's record (badge) and on the Leads List column.",
  },
];

export function EngarageEndpointsSetup({ integration }) {
  const apiKey = integration?.credentials?.api_key || "—";
  const [expanded, setExpanded] = useState("registration");

  const copy = async (text, label = "Copied!") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="engarage-endpoints">
      {/* Hero status */}
      <div className="p-4 rounded-xl border bg-emerald-50 border-emerald-200 flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-emerald-900">ENGARAGE is connected</p>
          <p className="text-xs text-emerald-700 mt-0.5">3 endpoints available below. Use the API Key tab to copy your authentication header.</p>
        </div>
      </div>

      {/* Endpoint cards */}
      {ENDPOINTS.map((ep) => {
        const Icon = ep.icon;
        const open = expanded === ep.id;
        const fullUrl = `${API_BASE}${ep.path}`;
        const curl = `curl -X ${ep.method} "${fullUrl}" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '${JSON.stringify(ep.sample, null, 2).replace(/'/g, "\\'")}'`;
        return (
          <div key={ep.id} className="border border-gray-200 rounded-xl bg-white overflow-hidden" data-testid={`engarage-ep-${ep.id}`}>
            <button onClick={() => setExpanded(open ? null : ep.id)} className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: ep.color + "15" }}>
                <Icon className="w-4 h-4" style={{ color: ep.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-gray-900">{ep.title}</p>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{ep.method}</span>
                  <code className="text-[10.5px] font-mono text-[#0D0D5B] truncate">{ep.path}</code>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{ep.desc}</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div className="px-3 pb-3 space-y-3 border-t border-gray-100 bg-gray-50/40">
                <div className="pt-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Endpoint URL</p>
                    <button onClick={() => copy(fullUrl, "URL copied")} className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-[#0D0D5B]" data-testid={`engarage-copy-url-${ep.id}`}>
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <code className="block bg-white border border-gray-200 rounded-lg p-2 text-xs font-mono text-gray-700 break-all">{fullUrl}</code>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Sample Request</p>
                    <button onClick={() => copy(curl, "cURL copied")} className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-[#0D0D5B]" data-testid={`engarage-copy-curl-${ep.id}`}>
                      <Copy className="w-3 h-3" /> Copy cURL
                    </button>
                  </div>
                  <pre className="bg-gray-900 text-emerald-200 rounded-lg p-3 text-[11px] font-mono leading-relaxed whitespace-pre-wrap break-all overflow-x-auto" style={{maxHeight: 240}}>{curl}</pre>
                </div>
                {ep.note && (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded-md text-[11px] text-amber-800">
                    <strong>Note:</strong> {ep.note}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 flex items-start gap-2">
        <ExternalLink className="w-4 h-4 shrink-0 mt-0.5" />
        <span>All three endpoints share the same <strong>X-API-Key</strong> header. Legacy keys from the previous Registration/Subscription integrations remain active and continue to work automatically.</span>
      </div>
    </div>
  );
}
